import "@testing-library/jest-dom";

import React from "react";
import { fireEvent, render, screen } from "@testing-library/react";
import { CanvasHierarchyGraphView } from "./CanvasHierarchyGraphView";
import { buildHierarchyGraph, HierarchyConfig } from "./hierarchyGraph";

const config: HierarchyConfig = {
  nodes: [
    {
      data_source_alias: "people",
      table_id: "employees",
      id_field: "id",
      label_field: "name",
    },
  ],
  relationships: [
    {
      relationship_ids: ["manager"],
      from_data_source_alias: "people",
      to_data_source_alias: "people",
      from_field: "manager",
      to_field: "id",
      reverse: true,
    },
  ],
  layout: "list",
  initial_depth: 2,
  max_depth: 3,
};
const graph = buildHierarchyGraph(config, {
  people: [
    { id: 0, _rowId: 0, name: "Director" },
    { id: 1, name: "Manager", manager: 0 },
    { id: 2, name: "Analyst", manager: 1 },
    { id: 3, name: "Orphan", manager: "hidden" },
  ],
});

it("collapses and expands with keyboard-operable buttons and retains real zero identity", () => {
  const open = jest.fn();
  render(
    <CanvasHierarchyGraphView graph={graph} config={config} onOpen={open} />,
  );
  expect(screen.getByRole("button", { name: "Open Director" })).toBeVisible();
  expect(
    screen.queryByRole("button", { name: "Open Analyst" }),
  ).not.toBeInTheDocument();
  fireEvent.click(screen.getByRole("button", { name: "Expand Manager" }));
  expect(screen.getByRole("button", { name: "Open Analyst" })).toBeVisible();
  fireEvent.click(screen.getByRole("button", { name: "Collapse Director" }));
  expect(
    screen.queryByRole("button", { name: "Open Manager" }),
  ).not.toBeInTheDocument();
  fireEvent.click(screen.getByRole("button", { name: "Open Director" }));
  expect(open).toHaveBeenCalledWith(
    expect.objectContaining({ recordId: 0, alias: "people" }),
  );
});

it.each(["tree", "list", "flow", "radial"] as const)(
  "%s keeps orphan information and relationship legend",
  (layout) => {
    render(
      <CanvasHierarchyGraphView graph={graph} config={{ ...config, layout }} />,
    );
    expect(screen.getByText("Parent unavailable")).toBeVisible();
    expect(screen.getByLabelText("Relationship kinds")).toHaveTextContent(
      "Relationship",
    );
    expect(
      screen.queryByRole("button", { name: "Open Director" }),
    ).not.toBeInTheDocument();
    expect(screen.getByLabelText("Hierarchy")).toHaveAttribute(
      "data-layout",
      layout,
    );
  },
);

it("shows cycle and shared-reference markers without recursing indefinitely", () => {
  const cyclic = buildHierarchyGraph(config, {
    people: [
      { id: 0, name: "A", manager: 1 },
      { id: 1, name: "B", manager: 0 },
    ],
  });
  render(
    <CanvasHierarchyGraphView
      graph={cyclic}
      config={{ ...config, initial_depth: 3 }}
    />,
  );
  expect(screen.getByText("Cycle reference")).toBeVisible();
});

it("labels partial graphs and never claims complete roots before all pages arrive", () => {
  render(<CanvasHierarchyGraphView graph={graph} config={config} partial />);
  expect(screen.getByRole("status")).toHaveTextContent("Partial hierarchy");
});

it("ArrowRight and ArrowLeft expand and collapse the focused item", () => {
  render(<CanvasHierarchyGraphView graph={graph} config={config} />);
  fireEvent.keyDown(screen.getByRole("group", { name: "Item Manager" }), {
    key: "ArrowRight",
  });
  expect(screen.getByText("Analyst")).toBeVisible();
  fireEvent.keyDown(screen.getByRole("group", { name: "Item Manager" }), {
    key: "ArrowLeft",
  });
  expect(screen.queryByText("Analyst")).not.toBeInTheDocument();
});

it("relationship kinds can be filtered without losing the permitted nodes", () => {
  render(<CanvasHierarchyGraphView graph={graph} config={config} />);
  fireEvent.click(screen.getByRole("checkbox", { name: "Relationship" }));
  expect(screen.getByText("Director")).toBeVisible();
  expect(screen.getByText("Manager")).toBeVisible();
});

it("a depth-limit control lets the user continue from that permitted node", () => {
  render(
    <CanvasHierarchyGraphView
      graph={graph}
      config={{ ...config, initial_depth: 1, max_depth: 1 }}
    />,
  );
  fireEvent.click(
    screen.getByRole("button", { name: "Continue from Manager" }),
  );
  expect(screen.getByText("Manager")).toBeVisible();
  expect(screen.queryByText("Director")).not.toBeInTheDocument();
});

it("ArrowDown and ArrowUp move keyboard focus between visible items", () => {
  render(<CanvasHierarchyGraphView graph={graph} config={config} />);
  fireEvent.keyDown(screen.getByRole("group", { name: "Item Director" }), {
    key: "ArrowDown",
  });
  expect(screen.getByRole("group", { name: "Item Manager" })).toHaveFocus();
  fireEvent.keyDown(screen.getByRole("group", { name: "Item Manager" }), {
    key: "ArrowUp",
  });
  expect(screen.getByRole("group", { name: "Item Director" })).toHaveFocus();
});

it("contains large layouts in a bounded scroll region instead of covering neighbouring elements", () => {
  render(<CanvasHierarchyGraphView graph={graph} config={config} />);
  expect(screen.getByLabelText("Hierarchy")).toHaveStyle({
    display: "flex",
    minHeight: "0",
  });
  expect(screen.getByRole("region", { name: "Hierarchy items" })).toHaveStyle({
    overflow: "auto",
    minHeight: "0",
  });
});

it("bounds rich tree cards so subtitles and actions cannot cover adjacent nodes", () => {
  render(
    <CanvasHierarchyGraphView
      graph={graph}
      config={{ ...config, layout: "flow" }}
    />,
  );
  expect(screen.getByRole("group", { name: "Item Director" })).toHaveStyle({
    height: "200px",
    overflow: "auto",
  });
});

it("places a radial root in the centre and its children on a surrounding ring", () => {
  const radialConfig = {
    ...config,
    layout: "radial",
    initial_depth: 3,
  } as unknown as HierarchyConfig;
  const radialGraph = buildHierarchyGraph(radialConfig, {
    people: [
      { id: 0, name: "Centre" },
      { id: 1, name: "North", manager: 0 },
      { id: 2, name: "South", manager: 0 },
    ],
  });
  render(
    <CanvasHierarchyGraphView graph={radialGraph} config={radialConfig} />,
  );
  expect(screen.getByRole("group", { name: "Item Centre" })).toHaveStyle({
    left: "370px",
    top: "380px",
  });
  expect(screen.getByRole("group", { name: "Item North" })).toHaveStyle({
    left: "370px",
    top: "30px",
  });
  expect(screen.getByRole("group", { name: "Item South" })).toHaveStyle({
    left: "370px",
    top: "730px",
  });
  expect(screen.getByRole("region", { name: "Hierarchy items" })).toHaveStyle({
    overflow: "auto",
  });
});

it("centres a radial viewport after expansion without resetting a user's unchanged view", () => {
  const clientWidth = jest
    .spyOn(HTMLElement.prototype, "clientWidth", "get")
    .mockReturnValue(500);
  const clientHeight = jest
    .spyOn(HTMLElement.prototype, "clientHeight", "get")
    .mockReturnValue(300);
  const radialConfig: HierarchyConfig = {
    ...config,
    layout: "radial",
    initial_depth: 3,
  };
  const radialGraph = buildHierarchyGraph(radialConfig, {
    people: [
      { id: 0, name: "Centre" },
      { id: 1, name: "North", manager: 0 },
      { id: 2, name: "South", manager: 0 },
    ],
  });
  try {
    const { rerender } = render(
      <CanvasHierarchyGraphView graph={radialGraph} config={radialConfig} />,
    );
    const region = screen.getByRole("region", { name: "Hierarchy items" });
    expect(region.scrollLeft).toBe(230);
    expect(region.scrollTop).toBe(330);
    region.scrollLeft = 10;
    rerender(
      <CanvasHierarchyGraphView
        graph={radialGraph}
        config={{ ...radialConfig }}
      />,
    );
    expect(region.scrollLeft).toBe(10);
    fireEvent.click(screen.getByRole("button", { name: "Collapse Centre" }));
    expect(region.scrollLeft).toBe(0);
    expect(region.scrollTop).toBe(0);
  } finally {
    clientWidth.mockRestore();
    clientHeight.mockRestore();
  }
});

it.each(["tree", "list", "flow", "radial"] as const)(
  "%s renders non-null avatar and icon from the configured View fields",
  (layout) => {
    const richConfig: HierarchyConfig = {
      ...config,
      layout,
      nodes: config.nodes.map((mapping) => ({
        ...mapping,
        avatar_field: "portrait_url",
        icon_field: "node_icon",
      })),
    };
    const richGraph = buildHierarchyGraph(richConfig, {
      people: [
        {
          id: 0,
          name: "Director",
          portrait_url: "https://example.invalid/fictional-director.png",
          node_icon: "person",
        },
      ],
    });
    render(<CanvasHierarchyGraphView graph={richGraph} config={richConfig} />);
    expect(screen.getByRole("img", { name: "Director" })).toHaveAttribute(
      "src",
      "https://example.invalid/fictional-director.png",
    );
    expect(screen.getByLabelText("Director icon")).toHaveTextContent("person");
  },
);
